import { Box, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import Header from '@components/typography/Header';
import useSSRDate from '@hooks/useSSRDate';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { FaRegCalendar } from 'react-icons/fa';
import danHeadshot from '../../../../public/headshots/dan_small.png';
import { FsmColors } from '../../../chakra-ui/fsmTheme';
import { Routes } from '../../../constants/routes';
import { decodeSlug } from '../../../utils/slugs';
import { SeoBreadcrumbs } from '../../articles/SeoBreadcrumbs';
import Button from '../../interactive/Button';
import Body2 from '../../typography/Body2';

export const RegionalHeading = ({
  title,
  subtitle,
  imageSrc,
  imageAlt,
  dateString,
  author = 'Daniel Petkevich',
  videoSrc = null,
}) => {
  const { formattedDate } = useSSRDate(dateString);
  const router = useRouter();
  const { stateSlug, citySlug } = router.query;
  const state = decodeSlug(stateSlug as string);
  const city = decodeSlug(citySlug as string);

  const breadcrumbs = [
    {
      text: 'Home',
      href: Routes.index,
    },
    {
      text: 'Best Medicare Supplement Plans',
      href: Routes.bestMedicareSupplement,
    },
    {
      text: state,
      href: `${Routes.bestMedicareSupplement}/${stateSlug}`,
    },
    {
      text: city,
      href: `${Routes.bestMedicareSupplement}/${stateSlug}/${citySlug}`,
      isCurrentPage: true,
    },
  ];

  return (
    <Stack w="100%" alignItems="stretch" spacing={4} maxW={['100%', '100%', '1200px', '1400px', '1600px']}>
      <SeoBreadcrumbs data={breadcrumbs} />
      <Flex w="100%" gap={[4, 4, 4, 6, 8]} justifyContent="center">
        <Stack spacing={4} maxW={['100%', '100%', '600px', '700px', '800px']} flex={1}>
          <Header as="h2">{title}</Header>

          {/* Author and Date */}
          {author && dateString && (
            <HStack gap={4}>
              <HStack spacing={2}>
                <Image
                  src={danHeadshot}
                  alt="Dan Petkevich"
                  width={36}
                  height={36}
                  style={{
                    borderRadius: 20,
                    border: `3px solid ${FsmColors.primary}`,
                  }}
                />
                <Body2>{author}</Body2>
              </HStack>
              <HStack spacing={2}>
                <FaRegCalendar size={16} />
                <Body2>{formattedDate}</Body2>
              </HStack>
            </HStack>
          )}
          {subtitle && (
            <Text fontSize={['md', 'lg']} color="gray.600" lineHeight="1.6">
              {subtitle}
            </Text>
          )}

          <Flex>
            <Button w="100%" maxW={['100%', '100%', '360px']} href={Routes.guideIntroduction}>
              Find Plans
            </Button>
          </Flex>
        </Stack>

        {/* Right Image */}
        {(imageSrc || videoSrc) && (
          <Box flex="1" maxW={['100%', '100%', '500px', '600px', '700px']} display={['none', 'none', 'block']}>
            {videoSrc ? (
              <Box w="100%" h={['240px', '300px', '400px']}>
                <iframe
                  title="video content"
                  src={videoSrc}
                  width="100%"
                  height="100%"
                  style={{ display: 'block', borderRadius: '10px' }}
                />
              </Box>
            ) : (
              <Box w="100%" h={['200px', '250px', '300px']} position="relative">
                <Image
                  src={imageSrc}
                  alt={imageAlt || 'Header Image'}
                  fill
                  style={{ objectFit: 'cover', borderRadius: '10px' }}
                  priority
                />
              </Box>
            )}
          </Box>
        )}
      </Flex>
    </Stack>
  );
};
